import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { apiMe, getToken, getMode, setMode, logout as doLogout } from "../lib/auth";
import { saveProfile } from "../lib/storage";

const AuthContext = createContext(null);

// Auto-logout after 10 minutes of inactivity
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
const INACTIVITY_FLAG_KEY = "mateaventura.lastInactivityLogout";

export function AuthProvider({ children }) {
  // status: 'loading' | 'cloud' | 'guest' | 'none'
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const statusRef = useRef(status);
  statusRef.current = status;

  const bootstrap = useCallback(async () => {
    const token = getToken();
    const mode = getMode();
    if (token) {
      try {
        const me = await apiMe();
        setUser({ id: me.id, username: me.username });
        if (me.profile) saveProfile(me.profile);
        setStatus("cloud");
        return;
      } catch (e) {
        doLogout();
      }
    }
    if (mode === "guest") {
      setStatus("guest");
      return;
    }
    setStatus("none");
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const performLogout = useCallback(() => {
    doLogout();
    try { localStorage.removeItem("mateaventura.profile"); } catch {}
    setUser(null);
    setStatus("none");
  }, []);

  // Inactivity timer: auto-logout after 10 minutes idle (cloud or guest)
  useEffect(() => {
    if (status !== "cloud" && status !== "guest") return;

    let timerId = null;
    const handleTimeout = () => {
      try { sessionStorage.setItem(INACTIVITY_FLAG_KEY, "1"); } catch {}
      performLogout();
    };
    const reset = () => {
      if (timerId) clearTimeout(timerId);
      timerId = setTimeout(handleTimeout, INACTIVITY_TIMEOUT_MS);
    };

    const events = ["mousedown", "touchstart", "keydown", "scroll", "click"];
    events.forEach((e) => document.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      if (timerId) clearTimeout(timerId);
      events.forEach((e) => document.removeEventListener(e, reset));
    };
  }, [status, performLogout]);

  const continueAsGuest = () => {
    setMode("guest");
    setStatus("guest");
    setUser(null);
  };

  const afterAuth = (data) => {
    setUser({ id: data.user.id, username: data.user.username });
    if (data.user.profile) saveProfile(data.user.profile);
    setStatus("cloud");
  };

  const logout = performLogout;

  return (
    <AuthContext.Provider
      value={{ status, user, continueAsGuest, afterAuth, logout, refresh: bootstrap }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { INACTIVITY_FLAG_KEY };
